/**
 * Coupon Strategy Factory
 * Manages registration and selection of coupon strategies based on coupon type
 */
import { couponTypes, couponSourceTypeMap, couponFetchStatus } from '../helper/constant';
import {
  timebaseCouponStrategy,
  marketingCouponStrategy,
  thresholdCouponStrategy,
  accountCouponStrategy,
  supportCouponStrategy,
} from '../strategies';
import { logger } from '@/utils/log';

/**
 * Factory class for managing coupon strategies
 * Implements the Strategy pattern with a factory for strategy selection
 */
class CouponStrategyFactory {
  constructor() {
    /**
     * Map of coupon type to strategy instance
     * @type {Map<string, import('../strategies/base-coupon-strategy').BaseCouponStrategy>}
     */
    this.strategies = new Map();

    /**
     * Default strategy for unknown types
     * @type {import('../strategies/base-coupon-strategy').BaseCouponStrategy|null}
     */
    this.defaultStrategy = null;

    // Register default strategies
    this.registerDefaultStrategies();
  }

  /**
   * Register all default strategies
   */
  registerDefaultStrategies() {
    this.register(couponTypes.TIMEBASE, timebaseCouponStrategy);
    this.register(couponTypes.MARKETING, marketingCouponStrategy);
    this.register(couponTypes.ACCOUNT, accountCouponStrategy);

    // Set timebase as default (most common type)
    this.defaultStrategy = timebaseCouponStrategy;
  }

  /**
   * Register a strategy for a coupon type
   * @param {string} type - Coupon type identifier
   * @param {import('../strategies/base-coupon-strategy').BaseCouponStrategy} strategy - Strategy instance
   */
  register(type, strategy) {
    if (!type || !strategy) {
      logger.warn('[CouponStrategyFactory] Invalid registration:', { type, strategy });
      return;
    }
    this.strategies.set(type, strategy);
  }

  /**
   * Unregister a strategy for a coupon type
   * @param {string} type - Coupon type identifier
   */
  unregister(type) {
    this.strategies.delete(type);
  }

  /**
   * Get strategy for coupon type
   * @param {string} couponType - Coupon type identifier
   * @returns {import('../strategies/base-coupon-strategy').BaseCouponStrategy|null} Strategy instance or null
   */
  getStrategy(couponType) {
    return this.strategies.get(couponType) || this.defaultStrategy;
  }

  /**
   * Get strategy based on coupon state (handles source type like threshold)
   * @param {Object} couponState - Coupon state object
   * @returns {import('../strategies/base-coupon-strategy').BaseCouponStrategy|null} Strategy instance
   */
  getStrategyForState(couponState) {
    const { couponType, couponSourceType } = couponState || {};

    // Special handling for threshold (subtype of marketing)
    if (couponType === couponTypes.MARKETING && couponSourceType === couponSourceTypeMap.threshold) {
      return thresholdCouponStrategy;
    }

    // Special handling for support coupon
    if (couponSourceTypeMap.isSupportCoupon(couponSourceType)) {
      return supportCouponStrategy;
    }

    return this.getStrategy(couponType);
  }

  /**
   * Calculate discount using appropriate strategy
   * @param {Object} couponState - Current coupon state
   * @param {Array} selectedPlans - Selected plans array
   * @param {Object} planWithAmountMap - Plan amount mapping
   * @param {string} verifyCouponStatus - Verification status
   * @param {Object} context - Additional context (store getters, etc.)
   * @returns {Object} Calculated discount result
   */
  calculateDiscount(couponState, selectedPlans, planWithAmountMap, verifyCouponStatus, context) {
    const strategy = this.getStrategyForState(couponState);

    if (!strategy) {
      return this.getDefaultResult();
    }

    // Only calculate for valid statuses
    const validStatuses = [couponFetchStatus.successfully, couponFetchStatus.pending];
    if (couponState.couponType === couponTypes.MARKETING && !validStatuses.includes(verifyCouponStatus)) {
      return this.getDefaultResult();
    }

    try {
      return strategy.calculateDiscount(couponState, selectedPlans, planWithAmountMap, context);
    } catch (error) {
      logger.error('[CouponStrategyFactory] Error calculating discount:', error);
      return this.getDefaultResult();
    }
  }

  /**
   * Convert API result to coupon state using appropriate strategy
   * @param {Object} apiResult - API response
   * @param {string} couponType - Coupon type to use
   * @param {Object} options - Conversion options
   * @returns {Object} Converted coupon state
   */
  convertToState(apiResult, couponType, options = {}) {
    const strategy = this.getStrategy(couponType);

    if (!strategy) {
      logger.warn('[CouponStrategyFactory] No strategy for type:', couponType);
      return null;
    }

    try {
      return strategy.convertApiResultToState(apiResult, options);
    } catch (error) {
      logger.error('[CouponStrategyFactory] Error converting to state:', error);
      return null;
    }
  }

  /**
   * Convert verify coupon API result to state (auto-detect type)
   * Replaces convertVerifyCouponResult2State
   * @param {Object} apiResult - Verify coupon API response
   * @param {Object} options - Conversion options (couponCode, allowChange)
   * @returns {Object} Coupon state
   */
  convertVerifyResultToState(apiResult, options = {}) {
    const detectedType = this.detectCouponType(apiResult);

    // For threshold, use threshold strategy
    if (apiResult?.thresholdList?.length > 0) {
      return thresholdCouponStrategy.convertApiResultToState(apiResult, options);
    }

    return this.convertToState(apiResult, detectedType, options);
  }

  /**
   * Convert campaign coupon result to state
   * Replaces convertCampaignCouponResult2State
   * @param {Object} marketingCoupon - Marketing coupon data from campaign link
   * @returns {Object} Coupon state
   */
  convertCampaignResultToState(marketingCoupon) {
    // Check if it's a threshold coupon
    if (marketingCoupon?.thresholdList?.length > 0) {
      return thresholdCouponStrategy.convertApiResultToState(marketingCoupon, {
        fromCampaign: true,
      });
    }

    // Use marketing strategy for campaign coupons
    return marketingCouponStrategy.convertApiResultToState(marketingCoupon, {
      fromCampaign: true,
    });
  }

  /**
   * Convert partner referral coupon result to state
   * Replaces convertPartnerReferCouponResult2State
   * @param {Object} coupon - Partner referral coupon data
   * @returns {Object} Coupon state
   */
  convertPartnerReferResultToState(coupon) {
    return marketingCouponStrategy.convertPartnerReferToState(coupon);
  }

  /**
   * Detect coupon type from API result
   * @param {Object} apiResult - API response
   * @returns {string} Detected coupon type
   */
  detectCouponType(apiResult) {
    if (!apiResult) {
      return couponTypes.NO_DISCOUNT;
    }

    // Threshold coupon detection
    if (apiResult.thresholdList && apiResult.thresholdList.length > 0) {
      return couponTypes.MARKETING;
    }

    // Account coupon detection
    if (apiResult.discount && !Array.isArray(apiResult)) {
      return couponTypes.ACCOUNT;
    }

    // Marketing coupon detection (from campaign)
    if (apiResult.discountInfo || apiResult.fromCampaignLink) {
      return couponTypes.MARKETING;
    }

    // Default to timebase
    return couponTypes.TIMEBASE;
  }

  /**
   * Detect source type from API result
   * @param {Object} apiResult - API response
   * @returns {string|undefined} Source type
   */
  detectSourceType(apiResult) {
    if (!apiResult) {
      return undefined;
    }

    // Threshold detection
    if (apiResult.thresholdList?.length > 0) {
      return couponSourceTypeMap.threshold;
    }

    // Partner refer detection
    if (apiResult.couponSourceType === couponSourceTypeMap.partnerRefer) {
      return couponSourceTypeMap.partnerRefer;
    }

    // Marketing detection
    if (apiResult.discountInfo) {
      return couponSourceTypeMap.marketing;
    }

    return undefined;
  }

  /**
   * Get default discount result structure
   * @returns {Object} Empty discount result
   */
  getDefaultResult() {
    return {
      annualDiscount: 0,
      monthlyDiscount: 0,
      quarterlyDiscount: 0,
      twoYearsDiscount: 0,
      threeYearsDiscount: 0,
      annualTermMonthlyDiscount: 0,
      totalDiscount: 0,
      discountPlans: {},
      thresholdResult: {},
    };
  }

  /**
   * Check if a strategy is registered for given type
   * @param {string} type - Coupon type
   * @returns {boolean} Whether strategy is registered
   */
  hasStrategy(type) {
    return this.strategies.has(type);
  }

  /**
   * Get all registered strategy types
   * @returns {Array<string>} Array of registered types
   */
  getRegisteredTypes() {
    return Array.from(this.strategies.keys());
  }

  /**
   * Get count of registered strategies
   * @returns {number} Number of registered strategies
   */
  getStrategyCount() {
    return this.strategies.size;
  }
}

// Export singleton instance
export const couponStrategyFactory = new CouponStrategyFactory();

// Export class for testing purposes
export { CouponStrategyFactory };
